import { inject, Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { ExtendLicenseDto, FilterFieldMap, Learner, LicenseWithCompletionResource, PayloadRequest, PayloadResponse, ToggleDownloadCertificateLicenseDto } from '../models';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { ApiService, DialogService } from '../../shared/services';
import { Router } from '@angular/router';
import { INVITE_LEARNER_FORM, LicenseForm } from '../models/form';
import { LearnerService } from './learner.service';
import { trimFormValues } from '../../shared/utils';

@Injectable({
	providedIn: 'root'
})
export class LicenseService {
	private apiService = inject(ApiService);
	private learnerService = inject(LearnerService);
	private dialogService = inject(DialogService);
	constructor(private router: Router) { }

	getLicensesList(
		filter?: FilterFieldMap,
		pageNumber?: number,
		pageSize?: number
	): Observable<PayloadResponse<LicenseWithCompletionResource[]>> {
		let params = new HttpParams();
		if (filter) {
			if (filter['reseller'])
				params = params.set('resellerId', filter['reseller'].value);
			if (filter['client'])
				params = params.set('clientId', filter['client'].value);
			if (filter['course'])
				params = params.set('courseId', filter['course'].value);
			if (filter['isDemo'])
				params = params.set('isDemo', filter['isDemo'].value);
		}

		if (pageNumber) params = params.set('pageNumber', pageNumber);
		if (pageSize) params = params.set('pageSize', pageSize);

		return this.apiService.get('licenses', { params: params });
	}

	loadLicenseData(
		licenseId: number,
		licenseData: LicenseForm,
		formLearner: FormGroup
	): Observable<any> {
		return this.getLicenseById(licenseId).pipe(
			tap(res => {
				licenseData.resellerCompany =
					res.data.clientPackage.resellerPackage.reseller.company;
				licenseData.clientBusinessName =
					res.data.clientPackage.client.businessName;
				licenseData.courseTitle =
					res.data.clientPackage.resellerPackage.course.title;
				licenseData.licenseActivationDate = res.data.activationDate;
				licenseData.licenseExpirationDate = res.data.expirationDate;
				if (res.data.learner) {
					formLearner.get('learnerId')?.setValue(res.data.learner.learnerId);
					formLearner.get('name')?.setValue(res.data.learner.name);
					formLearner.get('surname')?.setValue(res.data.learner.surname);
					formLearner.get('email')?.setValue(res.data.learner.email);
					formLearner.get('fiscalCode')?.setValue(res.data.learner.fiscalCode);
					formLearner.get('birthDate')?.setValue(res.data.learner.birthDate);
					formLearner.get('birthPlaceId')?.setValue(res.data.learner.birthPlaceId);
				}
			})
		);
	}

	getLicenseById(licenseId: number): Observable<any> {
		return this.apiService.get(`licenses/${licenseId}`);
	}

	countItems(isDemo?: boolean): Observable<{
		activeItems: number;
		activableItems: number;
		downloadItems: number;
	}> {
		let params = new HttpParams();
		if (isDemo)
			params = params.set('isDemo', isDemo);
		return this.apiService
			.get('licenses/count-status', { params: params })
			.pipe(map(payload => payload.data));
	}

	getAllResellersFromLicense(): Observable<
		PayloadResponse<{ id: number; name: string }[]>
	> {
		return this.apiService.get(`licenses/reseller-list`);
	}

	getAllClientsFromLicense(
		resellerId?: number
	): Observable<PayloadResponse<{ id: number; name: string }[]>> {
		let params = new HttpParams();
		if (resellerId) {
			params = params.set('resellerId', resellerId);
		}
		return this.apiService.get(`licenses/client-list`, { params: params });
	}

	getAllCoursesFromLicense(
		resellerId?: number
	): Observable<PayloadResponse<{ id: number; name: string }[]>> {
		let params = new HttpParams();
		if (resellerId) {
			params = params.set('resellerId', resellerId);
		}
		return this.apiService.get(`licenses/course-list`, { params: params });
	}

	navigateToList(): void {
		this.router.navigate(['/main/discenti']);
	}

	onSubmitLicenseLearnerForm(
		formLearner: FormGroup
	): Observable<PayloadResponse<Learner>> {

		trimFormValues(formLearner);

		if (formLearner.invalid) {
			formLearner.markAllAsTouched();
			this.dialogService.openDialogInfoNoRedirect(
				'Errore',
				'Compila tutti i campi obbligatori.'
			);
			return of();
		}
		
		const learner = this.learnerService.convertTypeForm(formLearner);
		return this.learnerService
			.updateLearner(learner)
			.pipe(tap(() => this.showDialogSuccessUpdateLearner()));
	}

	private showDialogSuccessUpdateLearner(): void {
		const dialogMessage =
			'Il discente associato alla licenza è stato modificato con successo';

		this.dialogService.openDialogAlert(
			'Operazione completata',
			dialogMessage,
			() => {
				this.router.navigate(['/main/discenti']);
			}
		);
	}

	countLicencesesByYear(): Observable<PayloadResponse<number>> {
		return this.apiService.get('licenses/count-by-year');
	}

	countLicencesesByMonth(): Observable<
		PayloadResponse<{ month: number; count: number }[]>
	> {
		return this.apiService.get('licenses/count-by-month');
	}

	countLicensesByReseller(): Observable<
		PayloadResponse<{ reseller: string; licenseCount: number }[]>
	> {
		return this.apiService.get('licenses/count-by-reseller');
	}

	// Invite learner

	getInviteLearnerForm(): Observable<any> {
		return of(INVITE_LEARNER_FORM);
	}

	// Resend all documents to Reseller, Client and Learner
	resendAllDocuments(licenseId: number): Observable<void> {
		return this.apiService.post(`licenses/${licenseId}/resend-documents`, {});
	}

	// Download certificate

	getCertificate(licenseId: number): Observable<Blob> {
		const options = {
			headers: new HttpHeaders({
				'Content-Type': 'application/pdf',
				'Accept': 'application/pdf'
			}),
			params: new HttpParams().set('format', 'pdf'),
			observe: 'body',
			responseType: 'blob',
		};
		return this.apiService.get(`course-template/js-report/${licenseId}`, options);
	}

	extendLicenseExpirationDate(extendLicenseDto: ExtendLicenseDto): Observable<void> {
		const payload = new PayloadRequest<ExtendLicenseDto>({ data: extendLicenseDto });
		return this.apiService.patch('licenses/extend', payload);
	}

	toggleEnableDownloadCertificate(ToggleDownloadCertificateLicenseDto: ToggleDownloadCertificateLicenseDto): Observable<void | boolean> {
		const payload = new PayloadRequest<ToggleDownloadCertificateLicenseDto>({ data: ToggleDownloadCertificateLicenseDto });
		return this.apiService.patch(`licenses/toggle-download-certificate`, payload)
	}
}
